import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import CommonPagination from "../Components/Pagination";
import {
  Box,
  Popover,
  Container,
  Tooltip,
  MenuItem,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Switch,
  CssBaseline,
  Autocomplete,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function Endpoint_details() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  /* ---------- ALERT ---------- */
  const [successMessage, setSuccessMessage] = useState("");
  const showAlert = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /* ---------- FORM ---------- */
  const initialFormData = {
    endpoint_id: "",
    cl_id: "",
    client_name: "",
    env_id: "",
    app_id: "",
    end_points: "",
    active_flag: 1,
  };
  const resetForm = () => {
    setFormData({
      endpoint_id: "",
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      app_id: "",
      end_points: "",
      active_flag: 1,
    });
    setFilteredApplications([]);
    setFormErrors({});
  };

  // Fetch applications filtered by environment
  const fetchApplicationsByEnvironment = async (env_id) => {
    if (!env_id) {
      setFilteredApplications([]);
      return;
    }

    try {
      const response = await apiService.get(
        `/endpoints/applications?env_id=${env_id}`,
      );
      setFilteredApplications(response);
    } catch (error) {
      console.error("Error fetching applications by environment:", error);
      setFilteredApplications([]);
    }
  };

  // Handle environment change with cascading validation
  const handleEnvironmentChange = async (event, newValue) => {
    const env_id = newValue ? newValue.env_id : "";

    setFormData((prev) => ({
      ...prev,
      env_id: env_id,
      app_id: "", // Clear application when environment changes
    }));

    // Fetch applications for the selected environment
    if (env_id) {
      await fetchApplicationsByEnvironment(env_id);
    } else {
      setFilteredApplications([]);
    }
  };

  // Handle application change
  const handleApplicationChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      app_id: newValue ? newValue.app_id : "",
    }));
  };
  const [tempFilters, setTempFilters] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    end_points: "",
  });
  // Handle edit with cascading validation
  const handleEdit = async (row) => {
    setFormData(row);
    setOriginalFormData({ ...row }); // Store original for change detection

    // Fetch applications for the selected environment when editing
    if (row.env_id) {
      await fetchApplicationsByEnvironment(row.env_id);
    }

    setIsEditing(true);
    setOpenDialog(true);
  };
  const [formData, setFormData] = useState(initialFormData);
  const [openDialog, setOpenDialog] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteEndpointId, setDeleteEndpointId] = useState(null);
  const [originalFormData, setOriginalFormData] = useState({});

  // Check if form has changed
  const hasFormChanged = () => {
    if (!isEditing) return true; // Always allow submit for new entries
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const handleClearForm = () => {
    setFormData(initialFormData);
    setIsEditing(false);
  };

  /* ---------- DROPDOWNS ---------- */
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  /* ---------- TABLE DATA ---------- */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  /* ---------- COLUMN VISIBILITY ---------- */
  const [visibleColumns, setVisibleColumns] = useState({
    cl_id: true,
    env_id: true,
    app_id: true,
    end_points: true,
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const columnLabels = {
    cl_id: "Client",
    env_id: "Environment",
    app_id: "Application",
    end_points: "Endpoint",
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.env_id) {
      errors.env_id = "Environment is required";
    }

    if (!formData.app_id) {
      errors.app_id = "Application is required";
    }

    if (!formData.end_points || formData.end_points.trim() === "") {
      errors.end_points = "Endpoint is required";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /* ---------- COLUMN FILTERS ---------- */
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [activeFilters, setActiveFilters] = useState({});

  const handleApplyColumnFilter = (col, value) => {
    setActiveFilters((prev) => ({ ...prev, [col]: value }));
    setFilterAnchorEls((prev) => ({ ...prev, [col]: null }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setSearchTerm("");
    setPage(1);
  };

  /* ---------- FETCH DATA ---------- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    // Fetch all data (non-paginated) for client-side filtering
    try {
      const result = await apiService.get(`/endpoints/list`);
      setData(result || []);
    } catch (err) {
      console.error("Error fetching endpoints:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients]);

  useEffect(() => {
    apiService.get("/endpoints/clients").then(setClients);
    apiService.get("/endpoints/environments").then(setEnvironments);
    apiService.get("/endpoints/applications").then(setApplications);
  }, []);

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, tempFilters, activeFilters]);

  /* ---------- FILTERED DATA ---------- */
  const filteredData = data.filter((row) => {
    // Search match: if searchTerm is empty, match all; otherwise check visible columns
    const searchMatch = searchTerm.trim() === "" 
      ? true 
      : Object.keys(visibleColumns)
          .filter((col) => visibleColumns[col])
          .some((col) =>
            String(row[col] || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
          );

    // Column filter match: check active filters
    const columnMatch = Object.entries(activeFilters).every(
      ([col, val]) =>
        !val ||
        String(row[col] || "")
          .toLowerCase()
          .includes(val.toLowerCase()),
    );

    return searchMatch && columnMatch;
  });

  // Client-side pagination on filtered data
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Update total to reflect filtered data count
  const filteredTotal = filteredData.length;

  /* ---------- CRUD HANDLERS ---------- */
  const handleSubmit = async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }
    const duplicate = data.some((row) => {
      const sameClient = row.cl_id === formData.cl_id;
      const sameEnv = row.env_id === formData.env_id;
      const sameApp = row.app_id === formData.app_id;

      const sameEndpoint =
        row.end_points?.trim().toLowerCase() ===
        formData.end_points?.trim().toLowerCase();

      const notSameRow = isEditing
        ? row.endpoint_id !== formData.endpoint_id
        : true;

      return sameClient && sameEnv && sameApp && sameEndpoint && notSameRow;
    });

    if (duplicate) {
      setDuplicateError("Endpoint already exists");
      return;
    } else {
      setDuplicateError(""); // clear if no duplicate
    }
    try {
      if (isEditing) {
        await apiService.put(`/endpoints/${formData.endpoint_id}`, formData);
        showAlert("Endpoint updated");
      } else {
        await apiService.post(`/endpoints`, formData);
        showAlert("Endpoint added");
      }

      setOpenDialog(false);
      handleClearForm();
      fetchData();
    } catch (err) {
      console.error(err);
      showAlert("Operation failed: " + (err.message || "Check console."));
    }
  };

  const handleDelete = (id) => {
    setDeleteEndpointId(id);
    setOpenDeleteDialog(true);
  };

  const handleToggleFlag = async (row) => {
    try {
      const newFlag = row.active_flag === 1 ? 0 : 1;

      await apiService.request(`/endpoints/${row.endpoint_id}/flag`, {
        method: "PATCH",
        body: JSON.stringify({ active_flag: newFlag }),
      });

      showAlert(
        `Endpoint ${newFlag === 1 ? "activated" : "deactivated"} successfully`,
      );

      fetchData();
    } catch (err) {
      console.error(err);
      showAlert("Flag toggle failed");
    }
  };
  /* ---------- UI ---------- */
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Endpoints Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
         
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={() => {
                resetForm();
                setIsEditing(false);
                setOpenDialog(true);
              }}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                filteredTotal,
              )} of ${filteredTotal}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(filteredTotal / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(filteredTotal / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "bold",
                }}
                flex={1}
              >
                {columnLabels[col]}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((p) => ({
                    ...p,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {Object.keys(visibleColumns).map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            <Box display="flex" alignItems="center">
                              {columnLabels[col]}
                              <IconButton
                                size="small"
                                // onClick={(e) => handleOpenColumnFilter(col, e)}
                                onClick={(e) =>
                                  setFilterAnchorEls((prev) => ({
                                    ...prev,
                                    [col]: e.currentTarget,
                                  }))
                                }
                              >
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={8} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((row) => (
                    <TableRow key={row.endpoint_id}>
                      <TableCell>
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          justifyContent="flex-start"
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleFlag(row)}
                          title="Toggle Active Flag"
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <FlagIcon
                              style={{
                                color:
                                  row.active_flag === 1 ? "#2E9F50" : "#FF0C0C",
                                width: 17,
                                height: 17,
                                transition: "color 0.2s",
                                verticalAlign: "middle",
                              }}
                            />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            title="Edit"
                            sx={{
                              color: "#2DB0E4",
                              "&:hover": { color: "#1976d2" },
                            }}
                          >
                            <EditOutlined sx={{ fontSize: "16px" }} />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.endpoint_id)}
                            title="Delete"
                            sx={{
                              color: "#666",
                              "&:hover": { color: "#d32f2f" },
                            }}
                          >
                            <DeleteIcon
                              style={{
                                width: 12,
                                height: 15,
                                cursor: "pointer",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>

                      {visibleColumns.cl_id && (
                        <TableCell>{row.client_name}</TableCell>
                      )}
                      {visibleColumns.env_id && (
                        <TableCell>{row.environment_name}</TableCell>
                      )}
                      {visibleColumns.app_id && (
                        <TableCell>{row.application_name}</TableCell>
                      )}
                      {visibleColumns.end_points && (
                        <TableCell>{row.end_points}</TableCell>
                      )}
                    </TableRow>
                  ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={8} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No endpoints found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
      </Box>
      {Object.keys(visibleColumns).map(
        (col) =>
          visibleColumns[col] && (
            <Popover
              key={col}
              open={Boolean(filterAnchorEls[col])}
              anchorEl={filterAnchorEls[col]}
              onClose={() =>
                setFilterAnchorEls((prev) => ({
                  ...prev,
                  [col]: null,
                }))
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <DialogContent>
                <TextField
                  label={`Filter ${columnLabels[col]}`}
                  value={tempFilters[col] || ""}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      [col]: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                />

                <DialogActions>
                  <Button
                    onClick={() => {
                      setTempFilters((prev) => ({
                        ...prev,
                        [col]: "",
                      }));
                      setActiveFilters((prev) => ({
                        ...prev,
                        [col]: "",
                      }));
                      setPage(1);
                      setFilterAnchorEls((prev) => ({
                        ...prev,
                        [col]: null,
                      }));
                    }}
                  >
                    Clear
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => {
                      setActiveFilters((prev) => ({
                        ...prev,
                        [col]: tempFilters[col],
                      }));
                      setPage(1);
                      setFilterAnchorEls((prev) => ({
                        ...prev,
                        [col]: null,
                      }));
                    }}
                  >
                    Filter
                  </Button>
                </DialogActions>
              </DialogContent>
            </Popover>
          ),
      )}

      {/* ---------- DIALOG ---------- */}
      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          // onClose={() => setOpenDialog(false)}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              py: 1,
            }}
          >
            {isEditing ? "Edit Endpoint" : "Add Endpoint"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box display="grid" gridTemplateColumns="repeat(2,1fr)" gap={1}>
              <TextField
                label="Client"
                value={
                  formData.client_name ||
                  clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                  ""
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                   minWidth: 320,
                  backgroundColor: "white",

                  "& .MuiInputBase-input": {
                    cursor: "not-allowed",
                    color: "grey",
                  },
                }}
              />
              <Autocomplete
                options={environments}
                getOptionLabel={(o) => o.name}
                value={
                  environments.find((e) => e.env_id === formData.env_id) || null
                }
                onChange={handleEnvironmentChange}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.875rem",
                      minHeight: "32px",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Environment"
                    error={!!formErrors.env_id}
                    required
                    helperText={formErrors.env_id}
                  />
                )}
              />
              <Autocomplete
                options={filteredApplications}
                getOptionLabel={(o) => o.name}
                value={
                  filteredApplications.find(
                    (a) => a.app_id === formData.app_id,
                  ) || null
                }
                onChange={handleApplicationChange}
                disabled={!formData.env_id || filteredApplications.length === 0}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.875rem",
                      minHeight: "32px",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Application"
                    error={!!formErrors.app_id}
                    required
                    helperText={formErrors.app_id}
                  />
                )}
              />

              <TextField
                label="End Point"
                name="end_points"
                value={formData.end_points}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    [e.target.name]: e.target.value,
                  });
                  setDuplicateError("");
                }}
                error={!!duplicateError}
                helperText={duplicateError}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenDialog(false);
                handleClearForm();
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={isEditing && !hasFormChanged()}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDeleteDialog(false);
            setDeleteEndpointId(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete Endpoint{" "}
              <strong>
                {
                  data.find((d) => d.endpoint_id === deleteEndpointId)
                    ?.end_points
                }
              </strong>{" "}
              ?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteEndpointId(null);
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(`/endpoints/${deleteEndpointId}`);
                  showAlert("Endpoint deleted");
                  fetchData();
                } catch (err) {
                  console.error(err);
                  showAlert("Delete failed");
                } finally {
                  setOpenDeleteDialog(false);
                  setDeleteEndpointId(null);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Container>
  );
}

export default Endpoint_details;
